import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import { identifyUser } from '@/helpers';
import checkLocalStorageForODTP from '@/helpers/checkLocalStorageForODTP';
import { getUserQueryOptions, setPendingOdtpParam } from '@/queries/users';
import { setSessionChecked, setSessionError, setUserData, unsetUserData } from '@/redux/slices/user/actions';
import { buildURL, patch } from '@/shared/fetch';
import { forgetPreviousUser, refreshPricesForNewSession } from '@/shared/forgetUser';
import { getClientId } from '@/shared/ga';
import { trackMP } from '@/shared/mp';
import { classifySessionTransition, readSessionWatermark, writeSessionWatermark } from '@/shared/sessionWatermark';

/**
 * Resolve who is signed in, and keep it resolved. Mount once per entry point.
 *
 * One react-query query owns the fetching and the caching; redux keeps a copy because most of the
 * app reads it from there. The copy is derived — every write here comes from an answer the query
 * received, never the other way round.
 *
 * This replaced three overlapping mechanisms: a hook that stopped asking once the session was
 * checked, a raw `fetch` in `guardHelpers`, and a separate cross-tab syncer. Two of them observed
 * the same cache entry with different options, and all three wrote the same redux keys.
 *
 * Cache settings follow `AppInitializer` in the educators app: the user is wanted for the whole
 * life of the app, so it never expires on a timer, and returning to the tab always re-asks. That
 * re-ask is what keeps tabs agreeing — signing in or out in one is picked up by the others.
 */
const cacheQueryOptions = {
  staleTime: Infinity,
  refetchOnWindowFocus: 'always',
} as const;

const updateUserGAClientIds = async (user: { gaClientIds?: string[]; _id?: string }) => {
  const clientId = await getClientId();
  const userClientIds = user.gaClientIds ?? [];
  if (!userClientIds.includes(clientId) && user?._id) {
    await patch(buildURL(`/users/${user._id}/clientId`), {
      gaClientIds: [...userClientIds, clientId],
    });
  }
};

/**
 * An ODTP on the landing URL is redeemed by the server as a side effect of reading the user.
 *
 * Captured here rather than in a route component because it has to be in hand before the query
 * fires, and this mounts above the router. It belongs to the URL the visitor arrived on, not to
 * whichever page happens to render first.
 */
const captureOdtp = () => {
  const odtpEntry = Array.from(new URL(document.location.href).searchParams).find(([key]) =>
    key.toLowerCase().includes('odtp'),
  );
  if (odtpEntry) {
    const [key, value] = odtpEntry;
    setPendingOdtpParam(checkLocalStorageForODTP(`${key}=${value}`));
  }
  return null;
};

const UserSessionSync = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  // A `useState` initialiser, not an effect: effects run after render, by which time the query
  // below has already been issued without the token.
  useState(captureOdtp);

  const { data, dataUpdatedAt, isSuccess, isError } = useQuery({
    ...getUserQueryOptions(),
    ...cacheQueryOptions,
  });

  /**
   * Name what the answer means, then do only what that transition asks for.
   *
   * Nothing here may depend on redux state. Depending on `logged` meant that signing up inside the
   * Speedy Flow re-ran this against the `null` still cached from page load — `staleTime: Infinity`
   * means the query does not refetch on its own — and signed the new account straight back out,
   * mid-checkout. The watermark and the query's own answer are the only inputs.
   *
   * `data` is `null`, not `undefined`, when nobody is signed in: the endpoint answers 204 and
   * `handleResponse` turns that into an explicit null. That separates "the server says there is no
   * user" from "we have not asked yet", and only the first signs the tab out. A failed request
   * reaches neither, because react-query keeps the last successful data on error.
   *
   * Every branch is safe to re-run on the same answer, so the effect needs no guard of its own: the
   * two that carry transition side effects (`signed-out`, `switched`) rewrite the watermark, which
   * reclassifies any repeat as `still-signed-out` or `refreshed`.
   */
  useEffect(() => {
    if (isError) {
      dispatch(setSessionError());
      return;
    }
    if (!isSuccess || !dataUpdatedAt) {
      return;
    }

    const nextUserId = data?._id ?? null;
    const transition = classifySessionTransition(readSessionWatermark(), nextUserId);

    // Nobody, and nobody before. Only the fact that we now *know* that is news — and it must not go
    // out as `unsetUserData`, which announces a logout that did not happen.
    if (transition === 'still-signed-out') {
      dispatch(setSessionChecked());
      return;
    }

    if (transition === 'signed-out' || transition === 'switched') {
      forgetPreviousUser(queryClient, nextUserId);
      /**
       * The reset that makes a *switch* safe, and it matters well beyond the cleanup: `setUserData`
       * merges over the previous user rather than replacing
       * (`subscription: { ...state.subscription, ...subscription }`), so without it any field the
       * new user's payload omits survives from the old one — including the subscription fields
       * `AuthGuard` reads to decide access.
       *
       * On a switch the pair is dispatched together on purpose. `unsetUserData` sets
       * `sessionChecked: true`, so the state in between is `sessionChecked && !logged` —
       * `AuthGuard`'s redirect condition exactly. react-redux batches the two into one render, so
       * `logged` goes `true -> true` and that effect never re-runs; if it ever stopped batching, a
       * switch would throw the user off a protected page. `userSessionSync.test` holds that up.
       */
      dispatch(unsetUserData());
      if (!data) return;
    } else {
      // Nothing of anyone else's is cached, so nothing is owed a Forget — but what is cached was
      // answered for a signed-out caller, and prices are not the same answer for both.
      if (transition === 'signed-in') {
        refreshPricesForNewSession(queryClient);
      }
      writeSessionWatermark(nextUserId);
    }

    if (!data) return;
    dispatch(setUserData(data));

    /**
     * Asserted on every answer that carries a user, not only on the transitions that bring one in.
     *
     * The two sides of that comparison do not live together: the watermark is this origin's
     * `localStorage` while Mixpanel's `distinct_id` is a cookie on `.zoolife.tv`, and the cookie is
     * the shorter-lived of the two. Lose it and the watermark still names the user, so every load
     * from then on classifies as `refreshed`, the identity is never reasserted, and the account's
     * events go to an anonymous id indefinitely with nothing to report it. `mixpanel.identify` with
     * the id it already holds is a no-op, so being wrong this way costs nothing.
     */
    identifyUser(data as { _id: string; role: string });

    // These two really are about arriving. The event announces a login, which did not happen on a
    // refresh; the client-id sync is a PATCH, and a window-focus refetch is no reason to send one.
    if (transition === 'signed-in' || transition === 'switched') {
      // Best-effort, and deliberately not awaited: the sync is a PATCH nothing on screen depends
      // on. Unhandled, though, a failed request became an unhandled rejection on every sign-in that
      // hit one.
      updateUserGAClientIds(data).catch((err) => console.error('Could not sync GA client ids', err));
      trackMP('login', { method: 'automatic' });
    }
  }, [data, dataUpdatedAt, dispatch, isError, isSuccess, queryClient]);

  return null;
};

export default UserSessionSync;
